import { z } from 'zod';
import { UploadTarget } from './files.js';
import { PersonRef } from './people.js';
import { WorkItemType } from './work-items.js';
/**
 * Importing work from other apps (docs/adr/0043-import-from-other-apps.md): Jira, Asana, ClickUp, Trello
 * and Monday.com. An owner or admin connects with an API token or uploads the app's export file; Chapsi
 * reads it and proposes a plan (projects → clients and projects, people, statuses, types); they adjust it
 * and start the import, which the worker runs. Tokens are sealed at once and never come back.
 */
export const WorkImportSource = z.enum(['jira', 'asana', 'clickup', 'trello', 'monday']);
export const WorkImportMethod = z.enum(['token', 'file']);
export const WorkImportStatus = z.enum(['awaiting_upload', 'analyzing', 'ready', 'running', 'done', 'failed', 'cancelled']);
/** Export files are small: CSV or JSON, at most 25 MB. */
export const WORK_IMPORT_FILE_MAX_BYTES = 25 * 1024 * 1024;
/** Items brought across when the owner does not say otherwise; the server's ceiling is WORK_IMPORT_MAX_ITEMS. */
export const WORK_IMPORT_DEFAULT_MAX_ITEMS = 5000;
export const WORK_IMPORT_MAX_ITEMS_CEILING = 20_000;
/** What each source's export file is. Excel exports are out of scope: Monday.com users save them as CSV. */
export const WORK_IMPORT_FILES = {
    jira: { kind: 'csv', pattern: /\.csv$/i, label: 'Jira CSV export' },
    asana: { kind: 'csv', pattern: /\.csv$/i, label: 'Asana CSV export' },
    clickup: { kind: 'csv', pattern: /\.csv$/i, label: 'ClickUp CSV export' },
    trello: { kind: 'json', pattern: /\.json$/i, label: 'Trello board JSON export' },
    monday: { kind: 'csv', pattern: /\.csv$/i, label: 'Monday.com board saved as CSV' },
};
export const WORK_IMPORT_SOURCE_LABEL = {
    jira: 'Jira',
    asana: 'Asana',
    clickup: 'ClickUp',
    trello: 'Trello',
    monday: 'Monday.com',
};
/** Jira Cloud sites only: https://<name>.atlassian.net. Nothing else is ever called. */
export const JIRA_SITE = /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.atlassian\.net\/?$/;
const secret = (label) => z.string().trim().min(8, `Paste the ${label}`).max(500).regex(/^[\x21-\x7e]+$/, `The ${label} has spaces or characters it should not`);
export const WorkImportCredentials = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('jira'),
        siteUrl: z.string().trim().toLowerCase().regex(JIRA_SITE, 'Use your Jira Cloud address, like https://yourteam.atlassian.net'),
        email: z.email().max(320),
        apiToken: secret('API token'),
    }),
    z.object({ source: z.literal('asana'), token: secret('personal access token') }),
    z.object({ source: z.literal('clickup'), token: secret('personal token') }),
    z.object({ source: z.literal('trello'), apiKey: z.string().trim().regex(/^[0-9a-f]{32}$/i, 'The API key is 32 letters and numbers'), token: secret('token') }),
    z.object({ source: z.literal('monday'), token: secret('API token') }),
]);
export const WorkImportProject = z.object({
    sourceId: z.string(),
    name: z.string(),
    /** Items found (bounded by the import's item limit while reading). */
    itemCount: z.number().int(),
    action: z.enum(['import', 'skip']),
    /** An existing client, or `newClientName` for a client created by the import. */
    clientId: z.uuid().nullable(),
    newClientName: z.string().nullable(),
    /** Set once imported. */
    projectId: z.uuid().nullable(),
});
export const WorkImportPersonAction = z.enum(['member', 'invite', 'name']);
export const WorkImportPerson = z.object({
    sourceId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    itemCount: z.number().int(),
    /** member: their work goes to `userId`; invite: invited as a member when the import starts; name: kept as a name only. */
    action: WorkImportPersonAction,
    userId: z.uuid().nullable(),
});
export const WorkImportStatusMap = z.object({ name: z.string(), itemCount: z.number().int(), statusId: z.uuid().nullable() });
export const WorkImportTypeMap = z.object({ name: z.string(), itemCount: z.number().int(), type: WorkItemType });
export const WorkImportOptions = z.object({
    maxItems: z.number().int().min(1).max(WORK_IMPORT_MAX_ITEMS_CEILING),
    comments: z.boolean(),
    /** Token imports only: export files only link to attachments. */
    attachments: z.boolean(),
});
export const WorkImportProgress = z.object({
    projectsDone: z.number().int(),
    projectsTotal: z.number().int(),
    items: z.number().int(),
    subtasks: z.number().int(),
    comments: z.number().int(),
    attachments: z.number().int(),
    skipped: z.number().int(),
});
export const WorkImportSkipReason = z.enum([
    'over_limit', 'already_imported', 'empty', 'project_skipped', 'too_large', 'blocked_type', 'not_available', 'download_failed',
    'needs_token', 'invite_failed', 'parent_missing',
]);
export const WorkImportSkipped = z.object({
    kind: z.enum(['item', 'comment', 'attachment', 'person']),
    sourceId: z.string(),
    label: z.string(),
    reason: WorkImportSkipReason,
});
export const WorkImport = z.object({
    id: z.uuid(),
    source: WorkImportSource,
    method: WorkImportMethod,
    status: WorkImportStatus,
    /** File imports: the export's name and size. */
    fileName: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    /** Jira token imports: the site. Never the email or the token. */
    site: z.string().nullable(),
    /** A sealed token is held for the worker. It is never returned. */
    hasCredentials: z.boolean(),
    createdBy: PersonRef.nullable(),
    projects: z.array(WorkImportProject),
    people: z.array(WorkImportPerson),
    statuses: z.array(WorkImportStatusMap),
    types: z.array(WorkImportTypeMap),
    options: WorkImportOptions,
    progress: WorkImportProgress,
    /** The first 500 items, comments, attachments and people that were not brought across, with why. */
    skipped: z.array(WorkImportSkipped),
    skippedTotal: z.number().int(),
    /** Shown when status is failed. Never contains imported text or the token. */
    error: z.string().nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
});
export const WorkImportList = z.object({
    items: z.array(WorkImport.omit({ projects: true, people: true, statuses: true, types: true, skipped: true })),
});
export const CreateWorkImportInput = z.discriminatedUnion('method', [
    z.object({ method: z.literal('token'), credentials: WorkImportCredentials }),
    z.object({
        method: z.literal('file'),
        source: WorkImportSource,
        fileName: z.string().trim().min(1).max(255).regex(/\.(csv|json)$/i, 'Choose the .csv or .json file the app gave you'),
        size: z.number().int().min(1).max(WORK_IMPORT_FILE_MAX_BYTES),
    }),
]);
export const CreateWorkImportResponse = z.object({ import: WorkImport, upload: UploadTarget.nullable() });
export const UpdateWorkImportPlanInput = z.object({
    projects: z
        .array(z.object({
        sourceId: z.string().max(200),
        action: z.enum(['import', 'skip']),
        clientId: z.uuid().nullable(),
        newClientName: z.string().trim().min(1).max(80).nullable(),
    }))
        .max(5000),
    people: z.array(z.object({ sourceId: z.string().max(200), action: WorkImportPersonAction, userId: z.uuid().nullable() })).max(20_000),
    statuses: z.array(z.object({ name: z.string().max(200), statusId: z.uuid() })).max(2000),
    types: z.array(z.object({ name: z.string().max(200), type: WorkItemType })).max(500),
    options: WorkImportOptions,
});
/** A token import whose token was deleted (it failed, or 24 hours passed) needs it again to start. */
export const StartWorkImportInput = z.object({ credentials: WorkImportCredentials.optional() });
//# sourceMappingURL=work-imports.js.map